import React, { useState, useEffect } from 'react';
import { HardDrive, ArrowLeft } from 'lucide-react';
//import { isTokenExpired, clearAuthData } from '../utils/tokenValidation';
import { customerApi, GausModel } from '../services/customerApi';
// Removed s3CredentialsManager - not needed for pre-signed URLs
import SceneDetailsPanel from '../components/SceneDetailsPanel';
import logo from '../assets/logo.gif';
import { useNavigate } from 'react-router-dom';
import { Viewer } from '@ydrive/gs-viewer';

const Gallery: React.FC = () => {
  const navigate = useNavigate();
  const [models, setModels] = useState<GausModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 3D Viewer state (using SceneManager approach)
  const [currentView, setCurrentView] = useState<'gallery' | '3d-viewer'>('gallery');
  const [selectedModel, setSelectedModel] = useState<GausModel | undefined>(undefined);
  const [modelLoading, setModelLoading] = useState(false);
  const modelContainerRef = React.useRef<HTMLDivElement>(null);
  const progressTextRef = React.useRef<HTMLParagraphElement>(null);
  const viewerRef = React.useRef<any>(null);

  // Component cleanup (no S3 sessions to clean up for pre-signed URLs)
  useEffect(() => {
    return () => {
      console.log('[Gallery] Component unmounting - cleaning up 3D viewer');
      // Only cleanup 3D viewer, no S3 sessions needed
    };
  }, []);

  // Fetch gaus models using enhanced customerApi with session pooling
  const fetchGalleryModels = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[Gallery] Fetching models from API...');

      // API now uses cookies for auth automatically
      const result = await customerApi.listGausModels();

      if (result.success) {
        setModels(result.data.objects);
        console.log(`[Gallery] Loaded ${result.data.objects.length} models`);
      } else {
        throw new Error('Failed to fetch gaus models');
      }
    } catch (err) {
      console.error('[Gallery] Error fetching gaus models:', err);

      // Handle unauthorized (401) gracefully
      if (err instanceof Error && (err as any).status === 401) {
        window.location.href = '/login';
        return;
      }

      setError(err instanceof Error ? err.message : 'Failed to load gaus library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGalleryModels();
  }, []);

  // Remove page visibility session cleanup - not needed for pre-signed URLs
  // Pre-signed URLs work independently of active sessions

  // ===== SCENEMANAGER APPROACH: 3D Viewer Management =====
  
  // Handle gaus model viewing (SceneManager style)
  const handleViewModel = async (model: GausModel) => {
    try {
      console.log(`[Gallery] Loading 3D viewer for model: ${model.fileName}`);
      
      // STEP 1: Cleanup any existing viewer
      if (viewerRef.current && viewerRef.current !== 'cleaning') {
        try {
          viewerRef.current.dispose();
        } catch (error) {
          console.warn('[Gallery] Error disposing previous viewer:', error);
        }
        viewerRef.current = null;
      }
      
      // STEP 2: Set view state (this will trigger the 3D viewer render)
      setCurrentView('3d-viewer');
      setSelectedModel(undefined);
      setModelLoading(true);
      
      // STEP 3: Wait for React to render the container
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // STEP 4: Check container exists with retries
      let attempts = 0;
      const maxAttempts = 10;
      
      while (!modelContainerRef.current && attempts < maxAttempts) {
        console.log(`[Gallery] Waiting for container, attempt ${attempts + 1}/${maxAttempts}`);
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!modelContainerRef.current) {
        throw new Error('3D viewer container not found in DOM after multiple attempts');
      }
      
      console.log('[Gallery] Container ready, creating viewer...');
      
      // STEP 5: Create viewer
      const viewer = new Viewer({
        sharedMemoryForWorkers: false,
        rootElement: modelContainerRef.current
      });
      viewerRef.current = viewer;
      // Track highest progress to avoid UI jumping back to 0% when processing starts
      let lastProgress = 0;
      
      // STEP 6: Load splat scene
      const splatBufferOptions = {
        showLoadingUI: false,
        onProgress: (progress: number) => {
          // Ignore any progress values lower than we've already displayed
          if (progress < lastProgress) return;
          lastProgress = progress;

          console.log(`[Gallery] Loading progress: ${progress}%`);
          if (progressTextRef.current) {
            progressTextRef.current.textContent = `Loading model... ${Math.round(progress)}%`;
          }
        }
      };

      console.log('[Gallery] Adding splat scene...');
      await viewer.addSplatScene(model.signedUrl, splatBufferOptions);
      
      console.log('[Gallery] Starting viewer...');
      viewer.start();
      
      // STEP 7: Update state
      setSelectedModel(model);
      setModelLoading(false);
      
      console.log(`[Gallery] 3D viewer loaded successfully for model: ${model.fileName}`);
    } catch (error) {
      console.error('[Gallery] Error loading 3D viewer:', error);
      setModelLoading(false);
      setSelectedModel(undefined);
      setCurrentView('gallery');
      alert(`Failed to load 3D viewer: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Close viewer function (SceneManager style)
  const close3DViewer = () => {
    console.log(`[Gallery] Closing 3D viewer`);
    
    // Dispose viewer properly
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        const viewer = viewerRef.current;
        viewerRef.current = 'cleaning' as any;
        
        if (typeof viewer.dispose === 'function') {
          viewer.dispose();
        }
        console.log('Viewer disposed successfully');
      } catch (error) {
        console.warn('[Gallery] Error disposing viewer:', error);
      }
      viewerRef.current = null;
    }
    
    // Reset all viewer state
    setSelectedModel(undefined);
    setModelLoading(false);
    setCurrentView('gallery');
    
    // Clear any loading text
    if (progressTextRef.current) {
      progressTextRef.current.textContent = '';
    }
  };

  // Handle escape key to close viewer (SceneManager style)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentView === '3d-viewer') {
        event.preventDefault();
        event.stopPropagation();
        close3DViewer();
      }
    };

    if (currentView === '3d-viewer') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [currentView]);

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      if (viewerRef.current && viewerRef.current !== 'cleaning') {
        try {
          if (typeof viewerRef.current.dispose === 'function') {
            viewerRef.current.dispose();
          }
        } catch (error) {
          console.warn('Unmount cleanup error (ignoring):', error);
        } finally {
          viewerRef.current = null;
        }
      }
    };
  }, []);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // ===== RENDER LOGIC (SceneManager style) =====
  
  // Render gallery view
  const renderGalleryView = () => (
    <div className="min-h-screen bg-[#161513]">
      <div className="relative">
        {/* Logo Background */}
        <div 
          className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${logo})` }}
        ></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
          {/* Section Header */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-white">
              Gallery
              {!loading && !error && (
                <span className="text-sm text-gray-300 ml-2">({models.length} models)</span>
              )}
            </h2>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)} 
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-20 bg-[#161513] shadow-lg border border-[#0CB4E4]/30">
              <div className="inline-block animate-spin h-8 w-8 border-b-2 border-[#0CB4E4] mb-4"></div>
              <h3 className="text-lg font-medium text-white mb-2">Loading Gallery...</h3>
              <p className="text-gray-300">Fetching your 3D scene</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-900/20 text-red-400 p-4 mb-4 font-medium border border-red-500/30">
              {error}
              <button 
                onClick={() => setError(null)} 
                className="ml-4 text-red-300 hover:text-red-200"
              >
                ✕
              </button>
            </div>
          )}

          {/* Models Grid */}
          {!loading && !error && models.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {models.map((model) => (
                <div
                  key={model.Key}
                  onClick={() => handleViewModel(model)}
                  className="relative overflow-hidden h-[320px] bg-[#161513] shadow-lg border border-[#0CB4E4]/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] rounded-xl group cursor-pointer"
                >
                  <div className="absolute inset-0 w-full h-full bg-[#161513] overflow-hidden rounded-xl">
                    <div className="h-full bg-gradient-to-br from-[#0CB4E4]/10 via-[#0CB4E4]/5 to-[#0CB4E4]/10 relative flex items-center justify-center">
                      <img 
                        src={logo} 
                        alt="3D Model" 
                        className="h-24 object-contain transition-transform duration-500 ease-out transform-origin-center group-hover:scale-110" 
                      />
                      <div className="absolute top-3 right-3 bg-[#161513]/90 backdrop-blur-sm px-2 py-1 text-xs font-medium text-gray-300 border border-[#0CB4E4]/30">
                        {model.fileType.toUpperCase()}
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#161513] via-[#161513]/70 to-transparent transition-opacity duration-300 rounded-xl opacity-80 group-hover:opacity-90"></div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-3 z-10 transition-all duration-300">
                    <h3 className="font-semibold text-white text-base mb-1 line-clamp-2" title={model.fileName}>
                      {model.fileName.replace(/\.(gaus|gauss|splat|ply)$/, '')}
                    </h3>
                    
                    <div className="space-y-2 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <HardDrive className="w-4 h-4 text-[#0CB4E4]" />
                        <span>{formatFileSize(model.Size)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && models.length === 0 && (
            <div className="text-center py-20 bg-[#161513] shadow-lg border border-[#0CB4E4]/30">
              <img 
                src={logo} 
                alt="No models found" 
                className="w-24 h-24 mx-auto mb-4 object-contain" 
              />
              <h3 className="text-lg font-medium text-white mb-2">No Models Found</h3>
              <p className="text-gray-300">Upload your first 3D model to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render 3D viewer (Gallery style with blue navigation panel)
  const render3DViewer = () => (
    <div className="fixed inset-0 w-full h-full bg-black z-[999]">
      {/* Close button for 3D viewer */}
      <div className="fixed top-4 right-4 z-[1001]">
        <button
          onClick={close3DViewer}
          className="bg-[#161513]/90 backdrop-blur-sm text-white px-6 py-3 shadow-lg hover:bg-[#161513] transition-all duration-200 flex items-center gap-3 font-medium border border-[#0CB4E4]/30 rounded-xl"
        >
          <span className="text-lg">✕</span>
          <span>Exit</span>
          <span className="text-xs text-gray-400">(ESC)</span>
        </button>
      </div>

      {/* Logo and brand overlay */}
      {selectedModel && !modelLoading && (
        <div 
          className="fixed top-4 left-4 z-[1001] flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            close3DViewer();
            navigate('/');
          }}
          title="Back to dashboard"
        >
          <img 
            src={logo} 
            alt="CR3ATE.AI" 
            className="h-10 w-10 object-contain" 
          />
          <h1 className="font-light text-xl text-[#0CB4E4] font-brand">CR3ATE.AI</h1>
        </div>
      )}

      {/* GALLERY'S BLUE NAVIGATION PANEL - Keep this exactly as Gallery has it */}
      {selectedModel && !modelLoading && (  
        <SceneDetailsPanel 
          model={{
            fileName: selectedModel.fileName,
            signedUrl: selectedModel.signedUrl,
            fileType: selectedModel.fileType,
            size: selectedModel.Size,
          }}
          onClose={close3DViewer}
        />
      )}

      {/* Model loading spinner */}
      {modelLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm z-[1002]">
          <div className="animate-spin h-16 w-16 border-4 border-[#0CB4E4] border-t-transparent mb-6"></div>
          <p ref={progressTextRef} className="text-white text-lg font-medium">
            Loading 3D model...
          </p>
          <p className="text-white/70 text-sm mt-2">
            Please wait while we load your scene
          </p>
        </div>
      )}

      {/* 3D Model Viewer Container */}
      <div
        ref={modelContainerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: selectedModel ? 1000 : -1,
          pointerEvents: selectedModel ? "auto" : "none",
          backgroundColor: "black"
        }}
      />
    </div>
  );

  // ===== MAIN RENDER =====
  return (
    <>
      {currentView === 'gallery' && renderGalleryView()}
      {currentView === '3d-viewer' && render3DViewer()}
    </>
  );
};

export default Gallery;
